/**
 * DocTOC
 *
 * Sticky sidebar table of contents. Tracks which DocSection is in view via
 * IntersectionObserver and highlights the matching entry. Click → smooth
 * scroll to the section.
 */
import { useEffect, useState } from 'react';
import { cn } from '@respectly/ui';
import { List } from 'lucide-react';

export interface DocTOCEntry {
  id: string;
  title: string;
}

export interface DocTOCProps {
  entries: DocTOCEntry[];
  className?: string;
}

export function DocTOC({ entries, className }: DocTOCProps) {
  const [activeId, setActiveId] = useState<string>(entries[0]?.id ?? '');

  useEffect(() => {
    if (entries.length === 0) return;

    const observer = new IntersectionObserver(
      (records) => {
        // Among the sections currently intersecting, pick the topmost one.
        const visible = records
          .filter((r) => r.isIntersecting)
          .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);
        const first = visible[0];
        if (first) {
          const id = (first.target as HTMLElement).id;
          if (id) setActiveId(id);
        }
      },
      {
        // Trigger when section's top is near the top of the viewport.
        rootMargin: '-15% 0px -70% 0px',
        threshold: 0,
      }
    );

    entries.forEach((entry) => {
      const el = document.getElementById(entry.id);
      if (el) observer.observe(el);
    });

    return () => observer.disconnect();
  }, [entries]);

  const handleClick = (e: React.MouseEvent<HTMLAnchorElement>, id: string) => {
    e.preventDefault();
    const el = document.getElementById(id);
    if (el) {
      el.scrollIntoView({ behavior: 'smooth', block: 'start' });
      setActiveId(id);
      // Update URL hash without jumping.
      if (history.replaceState) {
        history.replaceState(null, '', `#${id}`);
      }
    }
  };

  return (
    <>
      {/* Desktop sticky sidebar */}
      <aside
        className={cn(
          'hidden lg:block w-64 flex-shrink-0',
          className
        )}
      >
        <div className="sticky top-6">
          <p className="mb-3 px-3 text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400">
            On this page
          </p>
          <nav>
            <ul className="space-y-0.5">
              {entries.map((entry) => {
                const isActive = entry.id === activeId;
                return (
                  <li key={entry.id}>
                    <a
                      href={`#${entry.id}`}
                      onClick={(e) => handleClick(e, entry.id)}
                      className={cn(
                        'block px-3 py-1.5 rounded-md text-sm transition-colors border-l-2',
                        isActive
                          ? 'border-violet-500 bg-violet-50 dark:bg-violet-900/30 text-violet-700 dark:text-violet-300 font-medium'
                          : 'border-transparent text-slate-600 dark:text-slate-400 hover:bg-slate-100 dark:hover:bg-slate-800 hover:text-slate-900 dark:hover:text-white'
                      )}
                    >
                      {entry.title}
                    </a>
                  </li>
                );
              })}
            </ul>
          </nav>
        </div>
      </aside>

      {/* Mobile / tablet: collapsible <details> at top */}
      <details className="lg:hidden mb-4 rounded-xl border border-slate-200/60 dark:border-slate-700/60 bg-white dark:bg-slate-900 overflow-hidden">
        <summary className="flex items-center gap-2 px-4 py-3 cursor-pointer text-sm font-medium text-slate-700 dark:text-slate-300 select-none">
          <List className="w-4 h-4" />
          Jump to section
        </summary>
        <ul className="px-2 pb-2 max-h-[60vh] overflow-y-auto">
          {entries.map((entry) => (
            <li key={entry.id}>
              <a
                href={`#${entry.id}`}
                onClick={(e) => handleClick(e, entry.id)}
                className="block px-3 py-2 text-sm text-slate-700 dark:text-slate-300 hover:bg-slate-100 dark:hover:bg-slate-800 rounded-md"
              >
                {entry.title}
              </a>
            </li>
          ))}
        </ul>
      </details>
    </>
  );
}
